/**
 * Air `clean_kitchen` AIMS for between columns when it is free to choose an
 * island's width. Deliberately larger than `PANEL_SPACING.betweenGroups` (the
 * minimum a placement must clear): sized from scratch there is no reason to pack
 * an island to the limit, and this keeps a fresh auto-sized island clear of the
 * rules-engine clearance warnings.
 */
export const AUTO_RESIZE_TARGET_SPACING = 6;

export interface AutoResizeWidthInput {
	/** `maxOutsideWidth` of every column on the front panel (vents included, as `minimumWidth` counts them) */
	groupWidths: Array<number>;
	/** `frontPanel.overlapWidth` */
	overlapTotal: number;
	currentWidth: number;
	/** `frontPanel.minimumWidth` — or `getRequiredPanelWidthCfn` of a hypothetical set of columns */
	minimumWidth: number;
	/** `island.minimumSize?.width` — the width the island was loaded or last sized at */
	minimumSizeWidth: number | null;
	/** Never return a width below `currentWidth` (a drop must not shrink the island it lands on) */
	growOnly?: boolean;
}

export interface AutoResizeWidthResult {
	/** The run already holds its contents */
	fits: boolean;
	/** Width the island would be sized to if it had to change */
	idealWidth: number;
	/** The width to apply (equal to `currentWidth` when nothing should change) */
	finalWidth: number;
}

/**
 * The one answer to "how wide should this island become?" — used by
 * `clean_kitchen`'s auto-resize, by the drag-and-drop growth prediction the 3D
 * view shows BEFORE a drop, and by the drop itself, so the hint and the result
 * can never disagree.
 *
 * GROWS ONLY WHEN THE RUN CANNOT HOLD WHAT IS ON IT. When it fits, the island
 * keeps its width (or, unless `growOnly`, gives back width an earlier auto-size
 * borrowed — never below the width the customer chose, which every customer-facing
 * resize records as `minimumSize`).
 */
export const computeAutoResizeWidthCfn = (input: AutoResizeWidthInput): AutoResizeWidthResult => {
	const { groupWidths, overlapTotal, currentWidth, minimumWidth, minimumSizeWidth, growOnly = false } = input;

	const totalGroupWidth = groupWidths.reduce((sum, w) => sum + w, 0);
	const spacingAdjustment = groupWidths.length + 1;
	const targetWidth = totalGroupWidth + (spacingAdjustment * AUTO_RESIZE_TARGET_SPACING) + overlapTotal;
	const minWidth = minimumSizeWidth !== null && minimumSizeWidth !== undefined ? Math.max(36, minimumSizeWidth) : 36;
	const idealWidth = Math.max(Math.ceil(targetWidth), minWidth);

	const fits = currentWidth >= minimumWidth;
	const finalWidth = fits
		? (growOnly ? currentWidth : Math.min(currentWidth, idealWidth))
		: idealWidth;

	return { fits, idealWidth, finalWidth };
};
